import { FilterBrandTile } from "@/components/filters/filter-brand-tile"
import { CardImage } from "./card-image"
import { alternateCardImage } from "@/lib/card-image"
import Link from "next/link"
import { ArrowUpRightIcon } from "@/components/brand-kit/icon-sprite"
import { BadgeList } from "@/components/badges/badge-list"
import type { Badge as BadgeData } from "@/components/badges/select"
import { StarIcon } from "./icon-sprite"
import { dealCtaLabel, hasRenderedPrice } from "@/lib/deal-cta"
import { productCardCtaName, productCardLinkName } from "@/lib/card-link-names"
// FRIDGE-874 — the heart and the compare tick. These two are client components;
// this card remains server-compatible and renders those controls directly.
// Client carousels may also import it; rebate rails do so with compact display
// models while preserving the complete initial server-rendered card HTML.
import { SaveHeartButton, CompareToggle } from "@/components/chrome/save-compare-controls"
import { buildSavedItem } from "@/lib/saved-compare-item"

export interface CarouselProduct {
  brand: string
  title: string
  price: string
  originalPrice?: string
  badge?: string
  badgeColor?: string
  image: string
  /** Existing curated gallery, never constructed filenames. */
  images?: string[]
  href?: string
  rating?: number
  reviews?: number
  /**
   * Canonical badges (see docs/Page-Types-And-Templates.md). When this field is
   * DEFINED (even as an empty array) the card is in canonical mode: it renders
   * the set via the shared <BadgeList> (carousel variant, top-priority badge),
   * Real-or-Hidden — an empty array renders NOTHING, and the legacy
   * `badge`/`badgeColor` pill is never shown. When the field is undefined
   * (legacy callers), the old single-pill behavior is preserved.
   */
  badges?: BadgeData[]
  /**
   * FRIDGE-796 — what tells this row apart from another with the SAME title:
   * its model, or its minted number. Optional: appliance hrefs already encode a
   * FIN, so only callers whose URLs carry no number need to pass it (filters
   * and parts). Never rendered; it only reaches the links' accessible names.
   */
  identifier?: string
  /**
   * FRIDGE-1088 — FILTER rails only ("WHERE ARE LOGOS, FFN ETC AND PHOTOS FIX
   * SITEWIDE"). The brand's same-origin logo replaces the brand eyebrow text
   * (the name stays as its alt), and the (FFN) Fridge.com Filter Number
   * renders as the FFN chip under the title. Both optional and absent for
   * every appliance caller, so those cards are unchanged.
   */
  brandLogo?: string | null
  ffn?: string | null
}

interface ProductCarouselCardProps {
  product: CarouselProduct
  /** Let the image alt provide the same complete link name without duplicate attributes. */
  imageNamesLink?: boolean
  /** Additional classes on the outer wrapper */
  className?: string
  /** Optional scoped equivalents of the default utility lists; other callers are unchanged. */
  styleClasses?: Partial<Record<"shell" | "image" | "title" | "titleLink" | "cta" | "brand" | "price" | "imageFrame" | "info" | "priceRow" | "badgePosition" | "badge" | "frame" | "save" | "compare", string>>
}

export function ProductCarouselCard({ product, className = "", styleClasses = {} }: ProductCarouselCardProps) {
  // The image link has no text child (the title link sits below it), so it
  // needs an accessible name of its own — Semrush 08/16/2026 "Links with no
  // anchor text" (AI Search) counted every image-only card link. Names the
  // product and Fridge.com, the same form as the image alt.
  // FRIDGE-796 — the FIN goes in the name too. Two rows can carry one
  // manufacturer title, and a name that resolves to two URLs breaks WCAG 2.4.4.
  const imageLinkLabel = product.title
    ? (product.title.endsWith(" | Fridge.com")
        ? productCardLinkName(product.title.slice(0, -" | Fridge.com".length), product.href, " | Fridge.com", product.identifier)
        : productCardLinkName(product.title, product.href, " | Fridge.com", product.identifier))
    : undefined
  // FRIDGE-429 — ONE boolean gates the price row AND picks the CTA label, in
  // both the <Link> and the <button> variant below. Replaces the old inline
  // `product.price && product.price !== "$0"` check.
  const showsPrice = hasRenderedPrice(product.price)
  const ctaLabel = dealCtaLabel(showsPrice)
  // FRIDGE-874 — this shape has no row id, so the canonical path is the identity.
  // The store matches on id OR path, so the same product ticked on a PDP (which
  // does have the id) stays ONE entry.
  const savedItem = buildSavedItem({
    fin: product.identifier,
    brand: product.brand,
    title: product.title,
    image: product.image,
    href: product.href ?? '',
    price: product.price,
  })

  return (
    // Equal-height card alignment chain:
    //   - Carousel parent is `flex overflow-x-auto` (default align-items: stretch).
    //   - Outer wrapper is `flex flex-col` so the inner card inherits column flex.
    //   - Inner card is `flex-1 flex flex-col` — flex-1 fills the wrapper height
    //     reliably (percentage `h-full` chains can fail in Safari when the
    //     scroll container's height isn't explicit).
    //   - Info block has `flex-grow`; CTA group has `mt-auto` → button pinned
    //     to the bottom regardless of title length.
    <div className={`${styleClasses.frame ?? "flex-none w-[260px]"} snap-start group flex flex-col ${className}`}>
      <div className={styleClasses.shell ?? "bg-white rounded-lg overflow-hidden transition-all duration-500 hover:translate-y-[-6px] hover:shadow-xl hover:shadow-black/5 flex flex-col flex-1 relative"}>
        {/* Badge — canonical mode (badges defined) is Real-or-Hidden: empty
            array renders nothing, never an empty chip. Legacy pill only for
            callers that don't pass `badges`, and only when non-empty. */}
        {product.badges !== undefined ? (
          product.badges.length > 0 ? (
            <div className={styleClasses.badgePosition ?? "absolute top-3 left-3 z-10"}>
              <BadgeList badges={product.badges} variant="carousel" placement="inline" />
            </div>
          ) : null
        ) : product.badge ? (
          <div className={styleClasses.badgePosition ?? "absolute top-3 left-3 z-10"}>
            <span
              className={`${product.badgeColor || 'bg-gray-900'} ${styleClasses.badge ?? 'text-white px-2.5 py-1 text-[9px] font-bold tracking-widest rounded-full uppercase'}`}
            >
              {product.badge}
            </span>
          </div>
        ) : null}
        {/* Save (heart) — z-20, above the title link's stretched ::after overlay,
            so the tap saves instead of navigating. Only for a card that has a
            real product URL; without one there is nothing to save. */}
        {product.href ? (
          <div className={styleClasses.save ?? "absolute top-1 right-1 z-20"}>
            <SaveHeartButton item={savedItem} />
          </div>
        ) : null}
        {/* Image — `loading="lazy"` is load-bearing (FRIDGE-311, measured
            08/17/2026): React 19 emits a `<link rel="preload" as="image">` in
            the document head for EVERY non-lazy <img> it renders in the shell.
            This card is the rail card of every collection, article, filter,
            brand and blog-index page, and every rail sits below the fold — so a
            collection page shipped 46 product-image preloads (an article 33)
            in its <head>, all fetched before first paint alongside the CSS.
            Lazy images are still fetched promptly when they are inside the
            viewport at first layout; they just stop being preloaded. Guarded
            by tests/rail-images-not-preloaded.test.ts. */}
        <div className={styleClasses.imageFrame ?? "aspect-square bg-white overflow-hidden"}>
          <CardImage src={product.image} fallback={product.href?.startsWith("/filters/") ? <FilterBrandTile brand={product.brand} logo={product.brandLogo} code={product.identifier} /> : undefined} alternate={alternateCardImage(product.image, product.images)} href={product.href} alt={imageLinkLabel ?? product.title} width={260} height={260} />
        </div>
        {/* Info */}
        <div className={styleClasses.info ?? "p-4 flex flex-col flex-grow"}>
          <div className="mb-3">
            {/* Keep the brand eyebrow readable at 11px in company blue. */}
            {product.brandLogo ? (
              <span className="flex items-center" style={{ height: '1.1rem' }} data-card-brand-logo>
                {/* eslint-disable-next-line @next/next/no-img-element -- a brand logo of unknown aspect at eyebrow height */}
                <img src={product.brandLogo} alt={product.brand} loading="lazy" decoding="async" style={{ height: '100%', width: 'auto', maxWidth: '6rem', objectFit: 'contain' }} />
              </span>
            ) : (
              <span className={styleClasses.brand ?? "text-[11px] font-bold text-[#0469f0] tracking-wider uppercase"}>
                {product.brand}
              </span>
            )}
            {/* FRIDGE-325 — the card title is a LINK LABEL, not a document
                section, so it is a <p>, not an <h3>.

                This card is the rail card of every collection, article,
                filter, brand and blog-index page, and an article page renders
                three rails of it (relevant / bestsellers / top-rated) plus the
                recently-viewed carousel. As an <h3> that produced 68 H3
                elements on one article, 13 of them the same text at the same
                level — the Summit 108-can beverage cooler appeared at bytes
                67707, 165167 and 191574 of one document — which is what still
                blocked FRIDGE-149 item 2's "no repeated heading text" clause.

                Deduplicating products across rails would not have fixed it: the
                rails answer different questions and a product may legitimately
                be in more than one. The wrong part was calling a card title a
                heading at all. Nothing is lost for assistive technology — the
                title is the accessible NAME of the card link (pinned by
                tests/product-card-anchor-names.test.ts), which is where a link
                label belongs; the rail's own <ScrollCarousel> heading remains
                the section heading. Same reasoning, same repository precedent
                as TechnicalSpecs' titleAs="p" on the PDP.

                Every class is carried over verbatim, so this is semantics only
                — no visual change. Guarded by tests/product-card-titles-not-headings.test.ts. */}
            <p className={styleClasses.title ?? "text-base font-bold tracking-tight text-gray-900 leading-tight line-clamp-3"}>
              {product.href ? (
                // Primary card link — the stretched ::after overlay makes the
                // WHOLE card (image, title, price, gaps) navigate to the PDP as
                // a single unit. Previously only the image and CTA were links,
                // leaving the title and price dead. No nested-anchor invalidity:
                // the overlay is a pseudo-element, not a real <a>.
                <Link
                  href={product.href}
                  prefetch={false}
                  aria-label={productCardLinkName(product.title, product.href, "", product.identifier)}
                  className={styleClasses.titleLink ?? "text-inherit no-underline transition-colors hover:text-[#0469f0] after:absolute after:inset-0 after:content-['']"}
                >
                  {product.title}
                </Link>
              ) : (
                product.title
              )}
            </p>
            {/* FRIDGE-1088: the one-class FFN chip (app/globals.css .ffn-chip),
                inline rather than FfnBadge, so this card — the rail card of
                every collection, article and brand page — pulls no extra
                client module for a slot only filter rails fill. Real-or-Hidden:
                only a well-formed FFN renders. */}
            {product.ffn && /^FFN-\d{7}$/.test(product.ffn) ? (
              <div className="mt-1.5">
                <span className="ffn-chip">{product.ffn}</span>
              </div>
            ) : null}
            {typeof product.rating === "number" && (
              <div className="flex items-center gap-1 mt-1.5">
                <div className="flex">
                  {/* FRIDGE-179: shared <symbol>, not a path copy per star. */}
                  {[...Array(5)].map((_, i) => (
                    <StarIcon
                      key={i}
                      className={`w-3.5 h-3.5 ${i < Math.floor(product.rating!) ? "text-[#FFA41C]" : "text-gray-200"}`}
                    />
                  ))}
                </div>
                <span className="text-[10px] text-black">
                  {product.rating!.toFixed(1)}
                  {typeof product.reviews === "number" && ` (${product.reviews.toLocaleString()})`}
                </span>
              </div>
            )}
          </div>
          <div className="mt-auto">
            {showsPrice && (
              <div className={styleClasses.priceRow ?? "flex items-center gap-2 mb-3"}>
                <div className={styleClasses.price ?? "text-lg font-black text-gray-900 tracking-tighter"}>
                  {product.price}
                </div>
                {hasRenderedPrice(product.originalPrice) && (
                  <div className="text-xs text-gray-600 line-through">{product.originalPrice}</div>
                )}
              </div>
            )}
            {product.href ? (
              <Link href={product.href} prefetch={false} aria-label={productCardCtaName(ctaLabel, product, product.href)} className={styleClasses.cta ?? "relative z-10 w-full bg-[#ff9951] text-[var(--color-brand-action-text)] font-bold py-2.5 rounded-lg hover:bg-[#ff8533] active:scale-95 transition-all flex items-center justify-center gap-2 text-sm"}>
                {ctaLabel}
                <ArrowUpRightIcon className="w-4 h-4" />
              </Link>
            ) : (
              <button className="w-full bg-[#ff9951] text-[var(--color-brand-action-text)] font-bold py-2.5 rounded-lg hover:bg-[#ff8533] active:scale-95 transition-all flex items-center justify-center gap-2 text-sm">
                {ctaLabel}
                <ArrowUpRightIcon className="w-4 h-4" />
              </button>
            )}
            {/* Compare — `relative z-10` lifts it off the stretched link overlay. */}
            {product.href ? (
              <div className={styleClasses.compare ?? "relative z-10 mt-2"}>
                <CompareToggle item={savedItem} />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}
